import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { fetchReconMedia, submitReconReport, uploadReconMedia } from './api';
import type { ReconItem } from './types';

interface Props {
  caseId: string;
  memberId: string;
  items: ReconItem[];
  token: string;
  onUpdated: () => Promise<void>;
}

interface Draft {
  clientReportId: string;
  content: string;
  observedAt: string;
}

function localDateTime(date = new Date()): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function readDraft(key: string): Draft {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? '') as Partial<Draft>;
    if (typeof parsed.clientReportId === 'string' && typeof parsed.content === 'string' && typeof parsed.observedAt === 'string') {
      return parsed as Draft;
    }
  } catch {}
  return { clientReportId: crypto.randomUUID(), content: '', observedAt: localDateTime() };
}

function ReconItemForm({ caseId, memberId, item, token, onUpdated }: Props & { item: ReconItem }) {
  const storageKey = useMemo(() => `p2-recon-draft:${caseId}:${memberId}:${item.id}`, [caseId, memberId, item.id]);
  const [draft, setDraft] = useState<Draft>(() => readDraft(storageKey));
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{ mediaId: string; url: string } | null>(null);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(draft));
  }, [draft, storageKey]);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  function update(next: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...next }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    if (!navigator.onLine) {
      setMessage('目前離線，草稿已保存在這台裝置；恢復連線後再按一次送出。');
      return;
    }
    if (file && file.size > 10 * 1024 * 1024) {
      setMessage('圖片超過 10 MB，請換一張較小的圖片。');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const report = await submitReconReport(token, {
        caseId, itemId: item.id, clientReportId: draft.clientReportId,
        content: draft.content, observedAt: new Date(draft.observedAt).toISOString(),
      });
      if (file) await uploadReconMedia(token, report.id, file);
      localStorage.removeItem(storageKey);
      setDraft({ clientReportId: crypto.randomUUID(), content: '', observedAt: localDateTime() });
      setFile(null);
      form.reset();
      setMessage(file ? '文字與照片已一起送出。' : '場勘回報已送出。');
      try {
        await onUpdated();
      } catch {
        setMessage('回報已送出，但畫面更新失敗；請重新整理頁面查看。');
      }
    } catch (cause) {
      setMessage(`${cause instanceof Error ? cause.message : '送出失敗。'} 文字草稿仍保存在這台裝置，請直接重試。`);
    } finally {
      setBusy(false);
    }
  }

  async function showMedia(mediaId: string) {
    setBusy(true);
    try {
      const blob = await fetchReconMedia(token, mediaId);
      if (preview) URL.revokeObjectURL(preview.url);
      setPreview({ mediaId, url: URL.createObjectURL(blob) });
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : '圖片載入失敗。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="recon-item">
      <div className="recon-item-heading">
        <div><h3>{item.title}</h3><p>{item.description}</p></div>
        <span className="status-chip recon-status">{item.status === 'done' ? '已回報' : item.status === 'doing' ? '進行中' : '待處理'}</span>
      </div>
      {item.reportContent && <div className="latest-report"><strong>最近回報</strong><p>{item.reportContent}</p></div>}
      <form className="editor-form" onSubmit={handleSubmit}>
        <label>場勘時間<input type="datetime-local" value={draft.observedAt} required onChange={(event) => update({ observedAt: event.target.value })} /></label>
        <label>回報內容<textarea value={draft.content} rows={4} maxLength={5000} required onChange={(event) => update({ content: event.target.value })} /></label>
        <label>現場照片（選填，上限 10 MB）<input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></label>
        <p className="field-help">文字與選取的照片會由同一個按鈕送出；圖片需保持網路連線。</p>
        <button className="primary-button" disabled={busy}>{busy ? '處理中…' : file ? '送出文字與照片' : '送出回報'}</button>
      </form>
      {item.media.length > 0 && <div className="media-list">
        {item.media.map((media) => <button className="text-button" type="button" key={media.id} disabled={busy} onClick={() => void showMedia(media.id)}>查看 {media.fileName}</button>)}
      </div>}
      {preview && item.media.some((media) => media.id === preview.mediaId)
        && <img className="recon-preview" src={preview.url} alt={`${item.title} 場勘圖片`} />}
      <p className="field-help" aria-live="polite">{message || '輸入內容會先保存在這台裝置，離線時不會遺失。'}</p>
    </article>
  );
}

export default function ReconPanel(props: Props) {
  if (props.items.length === 0) return null;
  return (
    <section className="orders recon-panel" aria-labelledby="recon-title">
      <p className="section-kicker">現地回報</p>
      <h2 id="recon-title">我的場勘項目</h2>
      <p>文字與照片可一起回報；文字草稿支援離線保存，圖片送出時需有網路。</p>
      {props.items.map((item) => <ReconItemForm key={item.id} {...props} item={item} />)}
    </section>
  );
}
